import axios from './request';
import formData from '~/utils/formData';

/**
 * @typedef Report
 * @property {string} ID
 * @property {string} NAME
 * @property {string} SERVICE_NAME
 * @property {string?} STATUS
 * @property {string} CREATED
 * @property {boolean} REJECTED
 * @property {boolean} CONFIRMED
 * @property {boolean} CAN_EDIT_STATUS
 */

/** @enum */
export const Report = {
	Id: 'ID',
	Name: 'NAME',
	ServiceName: 'SERVICE_NAME',
	Status: 'STATUS',
	Created: 'CREATED',
	Rejected: 'REJECTED',
	Confirmed: 'CONFIRMED',
	CanUpdate: 'CAN_EDIT_STATUS',
}

/**
 * @typedef ReportsQuery
 * @property {string} year
 * @property {string} quarter
 * @property {string} month
 * @property {string} subject
 * @property {string} id
 * @property {string[]} status
 */

/**
 *
 * @param {ReportsQuery} params
 * @param {import('axios').AxiosRequestConfig?} config
 * @returns {Promise<{ data: { total: number, items: Report[] }, status: boolean }>}
 */
export async function getReports(params, config) {
	return axios
		.get('/api/Reports/getList/', {
			...config,
			params,
		})
		.then(({ data }) => data);
}

/**
 *
 * @param {string|number} id
 * @param {import('axios').AxiosRequestConfig?} config
 * @returns {Promise<{ data: any, status: boolean }>}
 */
export async function deleteReport(id, config) {
	return axios
		.delete('/api/Reports/delete/', {
			...config,
			params: {
				id,
			},
		})
		.then(({ data }) => data);
}

/**
 *
 * @param { string | number } id
 * @param { import('axios').AxiosRequestConfig? } config
 * @returns { Promise<{ data: any, status: boolean }> }
 */
export async function confirmReport(id, config) {
	return axios
		.patch('/api/Reports/confirm/', formData({
			[Report.Id]: id,
			[Report.Confirmed]: true,
		}), {
			...config,
			params: {
				id,
			},
		})
		.then(({ data }) => data);
}

/**
 *
 * @param { string | number } id
 * @param { import('axios').AxiosRequestConfig? } config
 * @returns { Promise<{ data: any, status: boolean }>}
 */
export async function rejectReport(id, config) {
	return axios
		.patch('/api/Reports/reject/', formData({
			[Report.Id]: id,
			[Report.Rejected]: true,
		}), {
			...config,
			params: {
				id,
			},
		})
		.then(({ data }) => data);
}

/**
 *
 * @param { ReportsQuery } params
 * @param { import('axios').AxiosRequestConfig? } config
 * @returns { Promise<{ data: any, status: boolean }> }
 */
export async function getExcel(params, config) {
	return axios
		.get('/api/Reports/getExcel/', {
			...config,
			responseType: 'blob',
			params,
		}).then(response => {
			const type = response.headers['content-type']
			const [, filename] = response.headers['content-disposition']?.split?.(';')
				.map?.((value) =>
					value.split('=').map(value => value.trim())
				)
				.find?.(([key]) => key ==='filename')
			const blob = new Blob([response.data], { type: type, encoding: 'UTF-8' })
			const link = document.createElement('a')
			link.href = window.URL.createObjectURL(blob)
			link.download = filename
			link.click()
		});
}

/**
 *
 * @param { string | number } id
 * @param { import('axios').AxiosRequestConfig? } config
 * @returns { Promise<{ data: any, status: boolean }> }
 */
export async function recreateReport(id, config) {
	return axios
		.patch('/api/Reports/create/', formData({ id }), {
			...config,
			params: {
				id,
			},
		}).then(({ data }) => data);
}

/**
 *
 * @param { ReportsQuery } params
 * @param { import('axios').AxiosRequestConfig? } config
 * @returns { Promise<{ data: any, status: boolean, error: string }> }
 */
export async function createReport(params, config) {
	return axios
		.post('/api/Reports/create/', formData(params), {
			...config,
			params,
		})
		.then(({ data }) => data);
}
